import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isNil, isEmpty, keys, has, first, isArray, isNull } from 'lodash';
// Import Types
import type { DropdownOption } from 'types';
import type { MenuItemDropdownRef } from 'components/MenuItemDropdown/types';
// Import Store
import { useAppSelector } from 'store';
import { selectCurrencyRates } from 'store/slices/currencyRates';
import { selectPageValidation } from 'store/slices/pageValidation';
import { selectUserSettingsCurrencyId } from 'store/slices/userData';
import { selectPartnerCurrencies } from 'store/slices/partnerSettings';
// Import UI components
import { Tabs, Tab, ExtendedInput, RadioGroup, Dropdown, Icon, Tooltip, Alert, BusyLoader } from '@geneui/components';
// Import Components
import CurrencyAutoFillSection from '../common/CurrencyAutoFillSection';
import MenuItemDropdown from '../MenuItemDropdown/MenuItemDropdown';
// Import Hooks
import { useMenuItem } from 'hooks/UseMenuItem';
import { useValidator } from 'hooks';
// Import Services
import { Helpers } from 'services';
// Import Enums
import { BonusCalculationTypesEnum, FormatNumberActionEnum, MenuItemIdEnum } from 'enums';
// Import Constants
import { l, DynamicBonusTypes, BonusTypesLabels } from 'constants/common';
import { DynamicBonusTypesRadioGroupOptionsConfig } from './configs';

const { getCurrencyLabel, getBitwiseValue, formatNumber } = Helpers;

const BonusCalculation = ({
    bonusType,
    bonusDetails,
    bonusDetailsAmounts,
    setBonusDetailsAmounts,
    radioGroupValue,
    setRadioGroupValue,
    bonusCalculationActiveTabKey,
    setBonusCalculationActiveTabKey,
    bonusCalculateActions,
    setBonusCalculateAction,
    bonusCalculateAction,
    bonusKind,
    setBonusKind,
    bonusKinds,
    considerLimits,
    setConsiderLimits,
    percentage,
    setPercentage,
    isVisibleRadioSelection,
    isDynamicDisabled,
    selectedWallet,
    setSelectedWallet,
    supportedCurrencies,
    supportedWallets,
}) => {
    const { t } = useTranslation();
    const validator = useValidator();

    const pageValidation = useAppSelector(selectPageValidation);
    const partnerCurrencies = useAppSelector(selectPartnerCurrencies);

    const [considerLimitsIds, setConsiderLimitsIds] = useState([]);
    const considerLimitsDropdownRef = useRef<MenuItemDropdownRef>(null);

    const secondaryCurrenciesDropdownData = useMemo(() => {
        if (isEmpty(partnerCurrencies)) {
            return [{ label: t(l.PrimaryWallet), value: null }];
        } else {
            const tmpResult: DropdownOption<Nullable<string>>[] = partnerCurrencies
                .filter((currency) => {
                    return currency.IsWalletAutocreatable;
                })
                .map((currency) => {
                    return {
                        label: currency.CurrencyId,
                        value: currency.CurrencyId,
                    };
                });
            tmpResult.unshift({ label: t(l.PrimaryWallet), value: null });
            return tmpResult;
        }
    }, [partnerCurrencies, t]);

    const supportedPartnerCurrencies = useMemo(() => {
        return partnerCurrencies.filter((currency) => currency.IsSupported);
    }, [partnerCurrencies]);

    const isCashOrCorrectionBonus = useMemo(() => {
        return [BonusTypesLabels.CashBonus, BonusTypesLabels.CorrectionBonus].includes(bonusType);
    }, [bonusType]);

    const currencyId = useAppSelector(selectUserSettingsCurrencyId);

    const totalCurrencyRates = useAppSelector(selectCurrencyRates);
    const currencyRates = useMemo(() => totalCurrencyRates?.[currencyId ?? ''], [totalCurrencyRates, currencyId]);
    const { convertedData: currenciesDropdownData, isLoading: isLoadingCurrencyData } = useMenuItem(104);

    const radioGroupChangeHandler = (event) => {
        setRadioGroupValue(event.target.value);
    };

    const bonusCalculationTabChangeHandler = (key) => {
        setBonusCalculationActiveTabKey(key);
    };

    const bonusKindChangeHandler = (event) => {
        setBonusKind({ ...bonusKind, [radioGroupValue]: event.value });
    };

    const bonusCalculateActionChangeHandler = (event) => {
        setBonusCalculateAction(event.value);
    };

    const percentageValueChangeHandler = (event) => {
        return setPercentage({ ...percentage, [radioGroupValue]: event.target.value });
    };

    const staticBonusDetailsChangeHandler = (currencyCode) => {
        return (event) => {
            setBonusDetailsAmounts((prev) => ({ ...prev, [currencyCode]: event.target.value.replace('-', '') }));
        };
    };

    const onAutoFillClickHandler = (data) => {
        const { amount, selectedCurrency } = data;
        const result = keys(bonusDetailsAmounts).reduce((acc, val) => {
            const tmpValue = (totalCurrencyRates?.[val][selectedCurrency].Rate ?? 0) * amount;
            const roundedValue = formatNumber(tmpValue, {
                type: FormatNumberActionEnum.CleanNumber,
                minPrecision: 9,
                maxPrecision: 9,
            });
            acc[val] = roundedValue;
            return acc;
        }, {});
        setBonusDetailsAmounts(result);
    };

    const isVisibleWalletSelection = useMemo(() => {
        return (
            ![BonusTypesLabels.PiePoints].includes(bonusType) &&
            !isEmpty(supportedWallets) &&
            ([BonusTypesLabels.FreeSpin, BonusTypesLabels.BoostBonus].includes(bonusType) ||
                supportedWallets.some((supportedWallet) => supportedCurrencies.includes(supportedWallet.CurrencyId)))
        );
    }, [supportedWallets, supportedCurrencies, bonusType]);

    const considerLimitsChangeHandler = (event) => {
        if (has(event, 'value')) {
            if (isArray(event.value)) {
                const value = event.value?.length > 1 ? getBitwiseValue(event.value) : first(event.value);
                setConsiderLimits({ ...considerLimits, [radioGroupValue]: value });
            }
        }
    };

    useEffect(() => {
        if (!isEmpty(considerLimitsIds)) {
            const resultIDs = considerLimitsIds.filter((item) => {
                return (considerLimits[radioGroupValue] & item) === +item;
            });

            if (considerLimitsDropdownRef?.current) {
                considerLimitsDropdownRef.current.updateValue?.(resultIDs);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [considerLimitsDropdownRef, radioGroupValue, considerLimitsIds]);

    const getDropdownData = (data) => {
        if (!isEmpty(data)) {
            setConsiderLimitsIds(data.map((item) => item.Id));
        }
    };

    const bonusInputLabel = useMemo(() => {
        return [
            BonusTypesLabels.NftVoucherBonus,
            BonusTypesLabels.WonderWheelSpin,
            BonusTypesLabels.FreeSpin,
            BonusTypesLabels.LoyaltyPoint,
        ].includes(bonusType)
            ? l.Count
            : l.Amount;
    }, [bonusType]);

    useEffect(() => {
        if (isCashOrCorrectionBonus) {
            validator({ selectedWallet: { selectedWallet: selectedWallet, amounts: bonusDetailsAmounts } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWallet, bonusDetailsAmounts]);

    const isVisibleWarningForUnsetCurrencies = useMemo(() => {
        const warningCurrencies: string[] = [];
        if (!isCashOrCorrectionBonus) {
            return [];
        }

        if (!isEmpty(bonusDetailsAmounts)) {
            supportedPartnerCurrencies.forEach(({ CurrencyId }) => {
                if (!(CurrencyId in bonusDetailsAmounts)) {
                    warningCurrencies.push(CurrencyId);
                }
            });
        }
        return warningCurrencies;
    }, [isCashOrCorrectionBonus, supportedPartnerCurrencies, bonusDetailsAmounts]);

    const isVisibleWarningUnsupportedBonusCurrency = useMemo(() => {
        const warningCurrencies: string[] = [];
        if (!isCashOrCorrectionBonus) {
            return [];
        }

        if (!isEmpty(bonusDetailsAmounts)) {
            keys(bonusDetailsAmounts).forEach((currency) => {
                const isCurrencySupported = supportedPartnerCurrencies.some((item) => item.CurrencyId === currency);
                if (!isCurrencySupported) {
                    warningCurrencies.push(currency);
                }
            });
        }
        return warningCurrencies;
    }, [isCashOrCorrectionBonus, supportedPartnerCurrencies, bonusDetailsAmounts]);

    return (
        <>
            {isVisibleWalletSelection && (
                <div className="wallet-selection-container">
                    <Dropdown
                        label={l.Wallet}
                        labelAppearance="swap"
                        value={selectedWallet}
                        onChange={(item) => setSelectedWallet(item.value)}
                        data={secondaryCurrenciesDropdownData}
                        hasSearch={true}
                        placeholder={l.Wallet}
                        className="wallet-dropdown"
                        isValid={isCashOrCorrectionBonus ? pageValidation.selectedWallet?.isValid : true}
                        errorText={isCashOrCorrectionBonus ? pageValidation.selectedWallet?.errorText : ''}
                    />
                    <Tooltip text={t(l.BonusWalletInfo)}>
                        <Icon type="bc-icon-info" />
                    </Tooltip>
                </div>
            )}
            {isVisibleRadioSelection && (
                <Tabs
                    type="button"
                    position="top"
                    activeKey={bonusCalculationActiveTabKey}
                    onChange={bonusCalculationTabChangeHandler}
                >
                    <Tab key={BonusCalculationTypesEnum.Static} title={t(l.Static)}>
                        <div className="bonus-calculation-static-tab-container">
                            {bonusDetails?.length > 1 && isNil(selectedWallet) && (
                                <CurrencyAutoFillSection
                                    dropdownData={currenciesDropdownData}
                                    onOk={onAutoFillClickHandler}
                                    isLoading={isLoadingCurrencyData}
                                    alertMessage={t(l.CurrencyAutoFillInfoAlert)}
                                />
                            )}
                            {!isEmpty(isVisibleWarningForUnsetCurrencies) && isNull(selectedWallet) && (
                                <Alert
                                    closable={false}
                                    title={t(l.UnsetBonusCurrenciesWarningMessage, {
                                        currenciesList: isVisibleWarningForUnsetCurrencies.join(', '),
                                    })}
                                    type="warning"
                                    className="partner-group-alert"
                                    iconProps={{ type: 'bc-icon-info' }}
                                />
                            )}
                            {!isEmpty(isVisibleWarningUnsupportedBonusCurrency) && isNull(selectedWallet) && (
                                <Alert
                                    closable={false}
                                    title={t(l.UnsupportedBonusCurrenciesWarningMessage, {
                                        currenciesList: isVisibleWarningUnsupportedBonusCurrency.join(', '),
                                    })}
                                    type="warning"
                                    className="partner-group-alert"
                                    iconProps={{ type: 'bc-icon-info' }}
                                />
                            )}
                            {/* // TODO: maybe dont show busyLoader in case when the currencies are not needed for bonus setup !!!     */}
                            <BusyLoader isBusy={isLoadingCurrencyData}>
                                {bonusDetails
                                    .filter(
                                        (x) =>
                                            isNil(selectedWallet) ||
                                            x.CurrencyCode === selectedWallet ||
                                            bonusType === BonusTypesLabels.FreeSpin,
                                    )
                                    .map(({ MinAmount, CurrencyCode, MaxAmount }, index) => (
                                        <div key={index} className="amount-wrap">
                                            <div className="amount-wrap-inner" key={index}>
                                                <div className="amount-text">
                                                    <p className="amount-text-code"> {CurrencyCode}</p>
                                                    <p className="amount-text-count">{`${t(
                                                        bonusInputLabel,
                                                    )} ${MinAmount} - ${MaxAmount}`}</p>
                                                </div>
                                                <ExtendedInput
                                                    type="number"
                                                    placeholder={t(bonusInputLabel)}
                                                    value={bonusDetailsAmounts[CurrencyCode]}
                                                    onChange={staticBonusDetailsChangeHandler(CurrencyCode)}
                                                    appearance="outline"
                                                    isValid={
                                                        isNil(
                                                            pageValidation[
                                                                CurrencyCode + BonusCalculationTypesEnum.Static
                                                            ]?.isValid,
                                                        ) ||
                                                        pageValidation[CurrencyCode + BonusCalculationTypesEnum.Static]
                                                            ?.isValid
                                                    }
                                                    errorText={t(
                                                        pageValidation[CurrencyCode + BonusCalculationTypesEnum.Static]
                                                            ?.errorText,
                                                        {
                                                            fieldName: t(bonusInputLabel),
                                                            count: pageValidation[
                                                                CurrencyCode + BonusCalculationTypesEnum.Static
                                                            ]?.paramValue,
                                                        },
                                                    )}
                                                    label={getCurrencyLabel(
                                                        t(bonusInputLabel),
                                                        bonusDetailsAmounts[CurrencyCode],
                                                        CurrencyCode,
                                                        currencyId,
                                                        currencyRates,
                                                    )}
                                                    labelAppearance="swap"
                                                />
                                            </div>
                                        </div>
                                    ))}
                            </BusyLoader>
                        </div>
                    </Tab>
                    <Tab key={BonusCalculationTypesEnum.Dynamic} title={t(l.Dynamic)} disabled={isDynamicDisabled}>
                        <div className="crm-bonus-calculation-dynamic-tab-container">
                            <div className="radio-group-wrapper">
                                <div
                                    className={classNames('radio-group-border-wrapper', {
                                        danger: pageValidation.radioGroupValue?.isValid === false,
                                    })}
                                >
                                    <RadioGroup
                                        name="controlled"
                                        options={DynamicBonusTypesRadioGroupOptionsConfig.map((item) => ({
                                            value: item.value,
                                            label: (
                                                <>
                                                    <p>{t(item.label)}</p>
                                                    <Tooltip text={t(item.tooltip)} position="bottom">
                                                        <Icon type="bc-icon-info" />
                                                    </Tooltip>
                                                </>
                                            ),
                                            disabled: false,
                                        }))}
                                        value={radioGroupValue}
                                        onChange={radioGroupChangeHandler}
                                    />
                                </div>
                                {pageValidation.radioGroupValue?.isValid === false && (
                                    <span className="radio-group-border-error-text">
                                        {pageValidation.radioGroupValue?.errorText}
                                    </span>
                                )}
                            </div>
                            <div className="bonus-items-wrap">
                                <div className="bonus-holder">
                                    {!isNil(radioGroupValue) && radioGroupValue !== DynamicBonusTypes.FromFile && (
                                        <>
                                            <div className="bonus-holder-title">
                                                <p className="ellipsis-text">{t(l.BonusKind)}</p>
                                                <Tooltip text={t(l.TooltipBonusCalculationBonusKind)} position="bottom">
                                                    <Icon type="bc-icon-info" />
                                                </Tooltip>
                                            </div>
                                            <Dropdown
                                                defaultValue={bonusKind[radioGroupValue]}
                                                isMultiSelect={false}
                                                disabled={false}
                                                inputSize="default"
                                                appearance="outline"
                                                data={bonusKinds[radioGroupValue]}
                                                onChange={bonusKindChangeHandler}
                                                isValid={
                                                    isNil(pageValidation.bonusKind?.isValid) ||
                                                    pageValidation.bonusKind?.isValid
                                                }
                                                errorText={pageValidation.bonusKind?.errorText}
                                            />
                                        </>
                                    )}
                                </div>
                                <div className="bonus-limits">
                                    <div className="bonus-holder">
                                        {radioGroupValue === DynamicBonusTypes.Object && (
                                            <>
                                                <div className="bonus-holder-title">
                                                    <p className="ellipsis-text">{t(l.BonusCalculateAction)}</p>
                                                    <Tooltip
                                                        text={t(l.TooltipBonusCalculationAction)}
                                                        position="bottom"
                                                    >
                                                        <Icon type="bc-icon-info" />
                                                    </Tooltip>
                                                </div>
                                                <Dropdown
                                                    defaultValue={bonusCalculateAction}
                                                    isMultiSelect={false}
                                                    disabled={false}
                                                    hasSearch={false}
                                                    inputSize="default"
                                                    placeholder="Dropdown title"
                                                    appearance="outline"
                                                    data={bonusCalculateActions}
                                                    onChange={bonusCalculateActionChangeHandler}
                                                    isValid={
                                                        isNil(pageValidation.bonusCalculateAction?.isValid) ||
                                                        pageValidation.bonusCalculateAction?.isValid
                                                    }
                                                    errorText={pageValidation.bonusCalculateAction?.errorText}
                                                />
                                            </>
                                        )}
                                    </div>
                                    <div className="percentage-block">
                                        {!isNil(radioGroupValue) && (
                                            <>
                                                <div className="bonus-holder-title">
                                                    <p className="ellipsis-text">{t(l.Percentage)}</p>
                                                    <Tooltip
                                                        text={t(l.TooltipBonusCalculationPercentage)}
                                                        position="bottom"
                                                    >
                                                        <Icon type="bc-icon-info" />
                                                    </Tooltip>
                                                </div>
                                                <ExtendedInput
                                                    type="number"
                                                    placeholder={t(l.Percentage)}
                                                    value={percentage[radioGroupValue]}
                                                    onChange={percentageValueChangeHandler}
                                                    appearance="outline"
                                                    isValid={
                                                        isNil(pageValidation.percentage?.isValid) ||
                                                        pageValidation.percentage?.isValid
                                                    }
                                                    errorText={pageValidation.percentage?.errorText}
                                                />
                                            </>
                                        )}
                                    </div>
                                    <div className="limits-check">
                                        <div className="bonus-holder-title">
                                            <p className="ellipsis-text">{t(l.ConsiderLimits)}</p>
                                            <Tooltip
                                                text={t(l.TooltipBonusCalculationConsiderLimits)}
                                                position="bottom"
                                            >
                                                <Icon type="bc-icon-info" />
                                            </Tooltip>
                                        </div>
                                        <MenuItemDropdown
                                            ref={considerLimitsDropdownRef}
                                            isMultiselect={true}
                                            menuItemId={MenuItemIdEnum.ConsiderLimits}
                                            getUpdate={considerLimitsChangeHandler}
                                            displayName={t(l.ConsiderLimits)}
                                            placeholder={t(l.ConsiderLimits)}
                                            buttonClassName="consider-limits-dropdown-button"
                                            getDropdownData={getDropdownData}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tab>
                </Tabs>
            )}
        </>
    );
};

BonusCalculation.propTypes = {
    bonusType: PropTypes.number.isRequired,
    bonusDetails: PropTypes.array.isRequired,
    bonusDetailsAmounts: PropTypes.object.isRequired,
    setBonusDetailsAmounts: PropTypes.func.isRequired,
    setRadioGroupValue: PropTypes.func.isRequired,
    bonusCalculationActiveTabKey: PropTypes.string.isRequired,
    setBonusCalculationActiveTabKey: PropTypes.func.isRequired,
    bonusCalculateActions: PropTypes.array.isRequired,
    setBonusCalculateAction: PropTypes.func.isRequired,
    setBonusKind: PropTypes.func.isRequired,
    bonusKinds: PropTypes.object.isRequired,
    percentage: PropTypes.object.isRequired,
    setPercentage: PropTypes.func.isRequired,
    considerLimits: PropTypes.object.isRequired,
    setConsiderLimits: PropTypes.func.isRequired,
    bonusKind: PropTypes.object,
    bonusCalculateAction: PropTypes.string,
    radioGroupValue: PropTypes.number,
    isDynamicDisabled: PropTypes.bool,
    isVisibleRadioSelection: PropTypes.bool,
    selectedWallet: PropTypes.string,
    setSelectedWallet: PropTypes.func,
    supportedCurrencies: PropTypes.array,
    supportedWallets: PropTypes.array,
};

export default memo(BonusCalculation);
